import { AlertTriangle, Save } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { useWatch } from "react-hook-form";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaAmountInput } from "@/shared/components/forms/ColocaAmountInput";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaRadioGroupField } from "@/shared/components/forms/ColocaRadioGroupField";
import { ColocaTextareaField } from "@/shared/components/forms/ColocaTextareaField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";
import type { LimitPayerType } from "@/shared/services/payments/paymentCorridors.service";
import { minorUnitsFor } from "@/shared/utils/currencyMinorUnits";
import { formatRail } from "@/shared/utils/railDisplay";

import { useLimitMutations } from "../../hooks/useLimitMutations";
import type { AmountLimitCell } from "../../types/paymentLimits.types";
import { rowFor, rowsInScope } from "./limitCellDisplay";
import {
  buildLimitFormSchema,
  type LimitFormValues,
  payerTypeOf,
  seedLimitForm,
  toUpsertPayload,
} from "./limitFormSchema";

export interface EditLimitIntent {
  cell: AmountLimitCell;
  /** Scope being written: null = the global rule, otherwise that organization's override. */
  organizationId: string | null;
  organizationName?: string;
  /** Which payer kind's row to seed from; null seeds from the both-kinds row. */
  payerType: LimitPayerType | null;
}

export interface EditLimitDialogProps {
  intent: EditLimitIntent;
  open: boolean;
  onClose: () => void;
  onSaved: (railLabel: string) => void;
}

/**
 * Sets the bounds of one corridor for one scope and one payer kind (or both). The form is seeded from the
 * live row of that exact scope so editing shows what is in force; switching the "applies to" radio changes
 * WHICH row gets written, and the backend keeps the superseded one as the audit trail. Amounts are decimal
 * strings at the currency precision — COP takes no cents — and at least one bound is required.
 *
 * Switching the radio RE-SEEDS the amounts from that kind's own row (empty when it has none, so an unbounded
 * kind reads as a rule being created). Without it, PSE — which ships one row per kind — would open on one
 * kind's cap and carry those figures into a save for the other: the company cap written as the individual's.
 */
export function EditLimitDialog({ intent, open, onClose, onSaved }: EditLimitDialogProps) {
  const { t } = useTranslations("paymentLimits");
  const { isSubmitting, error, setLimit } = useLimitMutations();
  const { cell, organizationId, organizationName, payerType } = intent;
  const maxFractionDigits = minorUnitsFor(cell.currency);

  const schema = useMemo(
    () =>
      buildLimitFormSchema(
        {
          money: t("validation_money"),
          atLeastOne: t("validation_at_least_one"),
          minBelowMax: t("validation_min_below_max"),
          fractionDigits: t("validation_fraction_digits", {
            currency: cell.currency,
            digits: String(maxFractionDigits),
          }),
        },
        maxFractionDigits,
      ),
    [t, cell.currency, maxFractionDigits],
  );
  const scopeRows = useMemo(() => rowsInScope(cell, organizationId), [cell, organizationId]);
  const seed = useMemo(() => seedLimitForm(rowFor(scopeRows, payerType), payerType), [scopeRows, payerType]);
  const form = useZodForm(schema, seed);

  // Only what the chosen kind actually holds is re-seeded; the reason follows it because it documents that
  // row. `reset` would also clobber the radio the user just moved.
  const selectedPayerType = useWatch({ control: form.control, name: "payerType" });
  const seededForRef = useRef(seed.payerType);
  useEffect(() => {
    if (selectedPayerType === seededForRef.current) return;
    seededForRef.current = selectedPayerType;
    const next = seedLimitForm(rowFor(scopeRows, payerTypeOf(selectedPayerType)), null);
    form.setValue("minAmount", next.minAmount, { shouldDirty: true });
    form.setValue("maxAmount", next.maxAmount, { shouldDirty: true });
    form.setValue("reason", next.reason, { shouldDirty: true });
    form.clearErrors(["minAmount", "maxAmount"]);
  }, [selectedPayerType, scopeRows, form]);

  const railLabel = formatRail(cell.rail);
  const directionLabel = t(cell.direction === "PAYIN" ? "direction_payin" : "direction_payout");

  const onSubmit = async (values: LimitFormValues) => {
    const failure = await setLimit(cell.rail, toUpsertPayload(values, cell, organizationId));
    if (!failure) onSaved(railLabel);
  };

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onClose();
      }}
      title={t("dialog_title", { rail: railLabel })}
      description={t(organizationId ? "dialog_scope_org" : "dialog_scope_global")}
    >
      <ColocaForm form={form} onSubmit={onSubmit} className="space-y-4">
        <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-border bg-card p-3">
          <ColocaBadge variant="secondary" className="font-sans">
            {railLabel}
          </ColocaBadge>
          <ColocaBadge variant="default" className="font-sans">
            {directionLabel}
          </ColocaBadge>
          <ColocaBadge variant="default" className="font-mono">
            {cell.currency}
          </ColocaBadge>
          <span className="ml-auto text-xs text-muted-foreground">
            {organizationId ? (organizationName ?? organizationId) : t("scope_global_label")}
          </span>
        </div>

        <ColocaRadioGroupField
          name="payerType"
          label={t("payer_type_label")}
          description={t("payer_type_hint")}
          orientation="horizontal"
          items={[
            { value: "BOTH", label: t("payer_both") },
            { value: "PERSON", label: t("payer_person") },
            { value: "COMPANY", label: t("payer_company") },
          ]}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <ColocaAmountInput
            name="minAmount"
            label={t("min_label")}
            suffix={cell.currency}
            maxFractionDigits={maxFractionDigits}
            placeholder={t("unbounded_placeholder")}
          />
          <ColocaAmountInput
            name="maxAmount"
            label={t("max_label")}
            suffix={cell.currency}
            maxFractionDigits={maxFractionDigits}
            placeholder={t("unbounded_placeholder")}
          />
        </div>

        <ColocaTextareaField
          name="reason"
          label={t("reason_label")}
          placeholder={t("reason_placeholder")}
          description={t("reason_internal_hint")}
          maxLength={500}
          rows={2}
        />

        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("dialog_consequence")} />

        {error && <ColocaAlert variant="destructive" text={error} />}

        <div className="flex justify-end gap-2 pt-2">
          <ColocaButton type="button" variant="ghost" onClick={onClose} disabled={isSubmitting}>
            {t("dialog_cancel")}
          </ColocaButton>
          <ColocaButton type="submit" variant="primary" icon={Save} loading={isSubmitting}>
            {t("dialog_submit")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </ColocaDialog>
  );
}
